// KEY-591: download / print a discussion transcript — for one item, one action point, a section, a whole dial, or a
// Group Key Dial with its children.
//
// Built entirely from what the reader already has on screen: every filtering decision (which threads they may see,
// which restricted messages they were addressed on) has already been made by the server, so an export can never
// hold more than the screen did. The document is a standalone HTML page with print styles, which a browser prints
// or saves as PDF, and which Word opens.
//
// Kept free of React so the document's shape and escaping can be pinned by plain unit tests.

import { isRichHtml, sanitizeRichText } from "@keydials/ui";

/** One message, already reduced to what the document shows. */
export interface ExportMessage {
  author: string;
  /** When it was said, already formatted for the reader. */
  at: string;
  /** The message body as stored: rich HTML (sanitised again here) or legacy plain text. */
  body: string;
  /** Where in the group this was said when the heading doesn't say — an item's name. */
  context?: string | null;
  /** Replaces the body for a deleted message ("Deleted by … · when"). */
  deleted?: string | null;
  /** The audience line of a restricted message; absent on a public one. */
  restricted?: string | null;
  /** 0 for a message, 1+ for a reply, so a thread keeps its shape on paper. */
  depth?: number;
}

export interface ExportGroup {
  /** A child dial's name, above the heading, on a group transcript. */
  dialName?: string | null;
  /** Where these were said: "Section › Action point". Omitted when the document's own title already says it. */
  heading?: string | null;
  messages: ExportMessage[];
}

export interface TranscriptDocument {
  /** What this is a transcript of — the first line of the page, and the file name. */
  title: string;
  /** Lines under the title: the dial, its organisation, the scope. */
  subtitle?: (string | null | undefined)[];
  groups: ExportGroup[];
  /** Said when the server capped what it returned. */
  note?: string | null;
  generatedAt?: Date;
}

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function bodyHtml(body: string): string {
  // Rich bodies are sanitised on the way in and again here; a plain body is escaped and keeps its line breaks.
  return isRichHtml(body) ? sanitizeRichText(body) : escapeHtml(body).replace(/\r?\n/g, "<br>");
}

function messageHtml(m: ExportMessage): string {
  const meta = [
    `<span class="author">${escapeHtml(m.author)}</span>`,
    m.at ? `<span class="at">${escapeHtml(m.at)}</span>` : "",
    m.context ? `<span class="chip">${escapeHtml(m.context)}</span>` : "",
    m.restricted && !m.deleted ? `<span class="chip restricted">Restricted</span>` : "",
  ].join("");
  const content = m.deleted ? `<div class="deleted">${escapeHtml(m.deleted)}</div>` : `<div class="body">${bodyHtml(m.body)}</div>`;
  const audience = m.restricted && !m.deleted ? `<div class="audience">${escapeHtml(m.restricted)}</div>` : "";
  const indent = Math.min(m.depth ?? 0, 6);
  return `<div class="message${m.restricted ? " is-restricted" : ""}"${indent ? ` style="margin-left:${indent * 18}px"` : ""}><div class="meta">${meta}</div>${content}${audience}</div>`;
}

/** The whole transcript as a standalone, printable HTML page. */
export function transcriptHtml(doc: TranscriptDocument): string {
  const generated = (doc.generatedAt ?? new Date()).toLocaleString(undefined, { dateStyle: "long", timeStyle: "short" });
  const subtitle = (doc.subtitle ?? []).filter((s): s is string => !!s && s.trim().length > 0);
  const count = doc.groups.reduce((n, g) => n + g.messages.length, 0);
  const groups = doc.groups
    .filter((g) => g.messages.length > 0)
    .map(
      (g) =>
        `<section>${g.dialName ? `<div class="dial">${escapeHtml(g.dialName)}</div>` : ""}${
          g.heading ? `<h2>${escapeHtml(g.heading)}</h2>` : ""
        }<div class="thread">${g.messages.map(messageHtml).join("")}</div></section>`,
    )
    .join("");
  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8">
<title>${escapeHtml(doc.title)} — discussion</title>
<style>
  body { font: 14px/1.5 -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; color: #1b1f24; max-width: 820px; margin: 32px auto; padding: 0 20px; }
  h1 { font-size: 22px; margin: 0 0 4px; }
  .sub { color: #555; margin: 0; }
  .generated { color: #777; font-size: 12px; margin: 10px 0 0; }
  .note { background: #fff7e0; border: 1px solid #e6d28a; padding: 8px 10px; border-radius: 6px; font-size: 13px; margin-top: 12px; }
  header { border-bottom: 2px solid #1b1f24; padding-bottom: 14px; margin-bottom: 22px; }
  section { margin: 0 0 22px; break-inside: avoid-page; }
  .dial { font-size: 12px; font-weight: 600; color: #666; text-transform: uppercase; letter-spacing: .04em; }
  h2 { font-size: 15px; margin: 2px 0 8px; }
  .thread { border-left: 2px solid #d6d9de; padding-left: 12px; display: flex; flex-direction: column; gap: 10px; }
  .message { break-inside: avoid; }
  .message.is-restricted { background: #f5f0fb; border: 1px solid #d9c9ef; border-radius: 6px; padding: 6px 9px; }
  .meta { display: flex; gap: 8px; align-items: baseline; flex-wrap: wrap; }
  .author { font-weight: 600; }
  .at { color: #777; font-size: 12px; }
  .chip { font-size: 11px; font-weight: 600; border: 1px solid #ccc; border-radius: 99px; padding: 0 7px; color: #555; }
  .chip.restricted { border-color: #b69ad9; color: #5b3a8a; }
  .body p { margin: 2px 0; }
  .deleted { color: #888; font-style: italic; }
  .audience { color: #666; font-size: 12px; margin-top: 3px; }
  .empty { color: #777; }
  @media print { body { margin: 0; max-width: none; } a { color: inherit; } }
</style></head><body>
<header><h1>${escapeHtml(doc.title)}</h1>${subtitle.map((s) => `<p class="sub">${escapeHtml(s)}</p>`).join("")}
<p class="generated">Discussion transcript · ${count} ${count === 1 ? "message" : "messages"} · produced ${escapeHtml(generated)}</p>${
    doc.note ? `<div class="note">${escapeHtml(doc.note)}</div>` : ""
  }</header>
${groups || `<p class="empty">Nothing has been said here yet.</p>`}
</body></html>`;
}

/** A file name for the download: the title, made safe, and the date. */
export function transcriptFileName(title: string, at: Date = new Date()): string {
  const slug =
    title
      .normalize("NFKD")
      .replace(/[^\w\s-]/g, "")
      .trim()
      .replace(/[\s_]+/g, "-")
      .replace(/-+/g, "-")
      .toLowerCase()
      .slice(0, 80) || "discussion";
  const date = `${at.getFullYear()}-${String(at.getMonth() + 1).padStart(2, "0")}-${String(at.getDate()).padStart(2, "0")}`;
  return `${slug}-discussion-${date}.html`;
}

/** Orders a thread oldest-first with each reply under the message it answers, and says how deep it sits. */
export function threadOrder<T extends { id: string; parentMessageId: string | null; createdAtUtc: string }>(
  messages: readonly T[],
): { message: T; depth: number }[] {
  const ids = new Set(messages.map((m) => m.id));
  const byParent = new Map<string | null, T[]>();
  for (const m of messages) {
    // A reply whose parent isn't in the list (not visible to this reader) is shown as a message in its own right.
    const parent = m.parentMessageId && ids.has(m.parentMessageId) ? m.parentMessageId : null;
    const list = byParent.get(parent) ?? [];
    list.push(m);
    byParent.set(parent, list);
  }
  for (const list of byParent.values()) {
    list.sort((a, b) => (a.createdAtUtc < b.createdAtUtc ? -1 : a.createdAtUtc > b.createdAtUtc ? 1 : 0));
  }
  const out: { message: T; depth: number }[] = [];
  const walk = (parent: string | null, depth: number) => {
    for (const m of byParent.get(parent) ?? []) {
      out.push({ message: m, depth });
      walk(m.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}

/**
 * The export's title for a place in a dial: "Dial › Section › Action point › Item", as far down as the place goes.
 * A key stands in for any part the structure no longer names.
 */
export function transcriptTitle(
  detail: {
    name: string;
    sections: readonly {
      key: string;
      label: string;
      showMnemonic?: boolean;
      segments: readonly { key: string; name: string; items?: readonly { key: string; name: string }[] | null }[];
    }[];
  },
  sectionKey?: string | null,
  segmentKey?: string | null,
  itemKey?: string | null,
): string {
  const parts = [detail.name];
  if (sectionKey) {
    const section = detail.sections.find((s) => s.key === sectionKey);
    parts.push(section ? (section.showMnemonic ? section.key : section.label) || section.key : sectionKey);
    if (segmentKey) {
      const segment = section?.segments.find((s) => s.key === segmentKey);
      parts.push(segment?.name || segmentKey);
      if (itemKey) {
        parts.push(segment?.items?.find((i) => i.key === itemKey)?.name || itemKey);
      }
    }
  }
  return parts.join(" › ");
}
